import type { PipelineStep } from './brief-generation.types';

/**
 * BE agent identifier for the brief-generation orchestrator.
 *
 * Source of truth: `apps/api/src/agents/use-cases/brief-generation/index.ts`
 * (`BriefGenerationAgentId`). Keep these in lock-step — drift breaks the
 * `createChatId` mint and the SSE transport.
 */
export const BRIEF_GENERATION_AGENT_ID = 'brief-generation-v1' as const;

/**
 * The workflow's sub-agents, each reported as its own `data-agent` node on the stream.
 *
 * Source of truth: `libs/agentic-toolkit/src/agents/brief-generation/contract.ts`. Mirrored here
 * rather than imported, as the orchestrator id above is: the contract's own barrel pulls the Relay
 * engine, which this app has no reason to bundle.
 */
export const BRIEF_GEN_EXTRACT_FILES_AGENT_ID = 'brief-gen-extract-files' as const;
export const BRIEF_GEN_ANALYZE_CONTEXT_AGENT_ID = 'brief-gen-analyze-context' as const;
export const BRIEF_GEN_COMPOSE_AGENT_ID = 'brief-gen-compose' as const;

/**
 * Which host generates the brief. Off keeps the original runtime in apps/api, reached through a
 * minted reasoner context; on posts to the workflow workspace-api serves, where the brief comes
 * back as the run's output and no context is minted.
 */
export const BRIEF_GENERATION_WORKSPACE_API_FLAG = 'brief_generation_workspace_api' as const;

/**
 * BE widget key emitted on `data-widget` capture for a finished brief.
 *
 * Source of truth: `libs/widgets/src/brief-generation/brief-generation.widget.schema.ts`
 * (`BriefGenerationWidgetKey`). Used by `useBriefGeneration` to filter the
 * incoming widget event before persisting via `useCreateBriefMutation`.
 */
export const BRIEF_WIDGET_KEY = 'brief-generation-widget:v1' as const;

/**
 * Ordered list of pipeline steps shown in the progress modal.
 *
 * - First 4 are emitted by the BE via `data-toolStart`/`data-toolComplete`
 *   stream events (see `lib/pipeline-steps.ts` for the label matcher).
 * - The 5th (`saving-brief`) is FE-only — its state is driven by the
 *   `useBriefGeneration` status machine: `pending` while preparing/
 *   uploading/streaming, `running` while persisting, `done` on success,
 *   `error` if the mutation fails.
 *
 * Order is significant — rows render top-to-bottom in this order.
 */
/* eslint-disable i18next/no-literal-string -- step matcher keys mirrored by the backend tracker; the progress UI renders `buildDisplaySteps` */
export const PIPELINE_STEPS: readonly PipelineStep[] = [
  { id: 'reading-files', label: 'Reading files' },
  { id: 'analyzing-inputs', label: 'Analyzing inputs' },
  { id: 'checking-knowledge-base', label: 'Checking knowledge base' },
  { id: 'composing-brief', label: 'Composing brief' },
  { id: 'saving-brief', label: 'Saving brief', feOnly: true },
] as const;
/* eslint-enable i18next/no-literal-string */
